import { InputField, SelectSimple } from '@advanza/input'
import { SelectSimpleChangeEvent } from '@advanza/input/interfaces/SelectSimpleChangeEvent'
import { Button } from '@advanza/ui'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import BaseModal, {
    BaseModalFooter,
    BaseModalHeader,
    BaseModalMainContent,
} from '@trustoo/components/modal/BaseModal'
import { useModalToast } from 'components/misc/Toaster'
import { TYPE_AMOUNT, TYPE_PERCENTAGE } from 'interfaces/PresenceSubscription'
import { useState } from 'react'

interface AddPresenceDiscountModalProps {
    onConfirm: (data: {
        type: typeof TYPE_AMOUNT | typeof TYPE_PERCENTAGE
        amount: number
        start_date?: string
        end_date?: string
    }) => Promise<void>
    onCancel: () => void
}

const AddPresenceDiscountModal = NiceModal.create(
    ({ onConfirm, onCancel }: AddPresenceDiscountModalProps) => {
        const modal = useModal()
        const [type, setType] = useState<typeof TYPE_AMOUNT | typeof TYPE_PERCENTAGE>(
            TYPE_PERCENTAGE,
        )
        const [amount, setAmount] = useState<number>(0)
        const [startDate, setStartDate] = useState<string>(new Date().toISOString().split('T')[0])
        const [endDate, setEndDate] = useState<string>('')
        const { addModalToast } = useModalToast()

        const handleConfirm = async () => {
            try {
                // Validate required fields
                if (!amount) {
                    addModalToast({
                        id: 'save-error',
                        msg: 'Missing required field: amount is required',
                        icon: 'error',
                        red: true,
                    })
                    return
                }

                // Validate amount
                if (amount <= 0) {
                    addModalToast({
                        id: 'save-error',
                        msg: 'Amount must be a positive number',
                        icon: 'error',
                        red: true,
                    })
                    return
                }

                // Validate start date if provided
                if (startDate) {
                    const startDateObj = new Date(startDate)
                    if (isNaN(startDateObj.getTime())) {
                        addModalToast({
                            id: 'save-error',
                            msg: 'Start date must be a valid date',
                            icon: 'error',
                            red: true,
                        })
                        return
                    }
                }

                // Validate end date if provided
                if (endDate) {
                    const endDateObj = new Date(endDate)
                    const startDateObj = startDate ? new Date(startDate) : new Date()
                    if (isNaN(endDateObj.getTime()) || endDateObj < startDateObj) {
                        addModalToast({
                            id: 'save-error',
                            msg: 'End date must be a valid date after the start date',
                            icon: 'error',
                            red: true,
                        })
                        return
                    }
                }

                await onConfirm({
                    type,
                    amount,
                    start_date: startDate || undefined,
                    end_date: endDate || undefined,
                })
                modal.resolve()
                modal.hide()
            } catch (error) {
                addModalToast({
                    id: 'save-error',
                    msg: error instanceof Error ? error.message : 'Failed to add discount',
                    icon: 'error',
                    red: true,
                })
            }
        }

        const handleCancel = () => {
            onCancel()
            modal.resolve()
            modal.hide()
        }

        return (
            <BaseModal onClose={handleCancel}>
                <BaseModalHeader>
                    <h2 className="text-display-xs font-bold">Add Discount</h2>
                </BaseModalHeader>
                <BaseModalMainContent>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-md font-medium text-neutral-700 mb-1">
                                Discount Type
                            </label>
                            <SelectSimple
                                value={type}
                                onChange={(event: SelectSimpleChangeEvent) =>
                                    setType(
                                        event.target.value as
                                            | typeof TYPE_AMOUNT
                                            | typeof TYPE_PERCENTAGE,
                                    )
                                }
                                options={[
                                    { value: TYPE_AMOUNT, name: 'Amount' },
                                    { value: TYPE_PERCENTAGE, name: 'Percentage' },
                                ]}
                            />
                        </div>
                        <div>
                            <label className="block text-md font-medium text-neutral-700 mb-1">
                                Amount
                            </label>
                            <InputField
                                type="number"
                                value={amount}
                                onChange={(e) => setAmount(parseFloat(e.target.value))}
                                min={0}
                                step={type === TYPE_PERCENTAGE ? 1 : 0.01}
                            />
                        </div>
                        <div>
                            <label className="block text-md font-medium text-neutral-700 mb-1">
                                Start Date
                            </label>
                            <InputField
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                min={new Date().toISOString().split('T')[0]}
                            />
                        </div>
                        <div>
                            <label className="block text-md font-medium text-neutral-700 mb-1">
                                End Date
                            </label>
                            <InputField
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                min={startDate || new Date().toISOString().split('T')[0]}
                            />
                        </div>
                    </div>
                </BaseModalMainContent>
                <BaseModalFooter>
                    <div className="flex justify-end gap-2">
                        <Button appearance="secondary" onClick={handleCancel}>
                            Cancel
                        </Button>
                        <Button appearance="primary" onClick={handleConfirm}>
                            Add Discount
                        </Button>
                    </div>
                </BaseModalFooter>
            </BaseModal>
        )
    },
)

export default AddPresenceDiscountModal
